import { useCallback, useMemo, useState } from "react"

import { type DownloadableTag } from "@forento/shared/models/downloadable"
import { getExtensionByFileName } from "@forento/shared/utilities/file"
import { parseNumber } from "@forento/shared/utilities/number"
import { toSlug } from "@forento/shared/utilities/string"

import { useAlert } from "~/contexts/AlertContext"
import { useTranslation } from "~/translations"
import { getProductPriceLabel } from "~/utilities/product"
import { query } from "~/utilities/trpc"

export interface Downloadable {
	id: number
	name: string
	thumbnailUrl: string | null
	shortDescription: string | null
	priceLabel: { color: "orange" | "blue"; value: string }
	tagIds: number[]
}

type UseDownloadableResult =
	| { status: "error" | "loading" }
	| { status: "success"; tags: DownloadableTag[]; data: Downloadable[] }
export function useDownloadables(): UseDownloadableResult {
	const t = useTranslation()

	const { data: downloadables, error: downloadablesError } = query.downloadable.listPersonalized.useQuery()
	const { data: tags, error: tagsError } = query.downloadable.listTags.useQuery()

	return useMemo(() => {
		if (downloadablesError || tagsError) return { status: "error" as const }
		if (downloadables === undefined || tags === undefined) return { status: "loading" as const }
		return {
			status: "success",
			tags,
			data: downloadables.map(downloadable => ({
				id: downloadable.id,
				name: downloadable.name,
				thumbnailUrl: downloadable.thumbnailUrl,
				shortDescription: downloadable.shortDescription,
				priceLabel: getProductPriceLabel(downloadable, t),
				tagIds: downloadable.tagIds,
			})),
		}
	}, [downloadables, downloadablesError, tags, tagsError, t])
}

export function useDownloadable(id: number) {
	const alert = useAlert()
	const t = useTranslation()

	const { data: downloadable, error, refetch } = query.downloadable.getPersonalized.useQuery(id)

	const download = query.downloadable.download.useMutation()

	const [downloadProgress, setDownloadProgress] = useState<number>()

	const handleDownload = useCallback(async () => {
		if (downloadable?.status !== "unlocked") return

		const link = await download.mutateAsync(id)
		if (link === null) return

		if (link.type === "url") {
			window.open(link.url, "_blank")
			return
		}

		setDownloadProgress(0)

		try {
			const response = await fetch(link.filePath, {
				headers: { "Content-Type": "application/octet-stream" },
			})
			if (response.body === null) return
			const reader = response.body.getReader()

			const contentLength = parseNumber(response.headers.get("Content-Length") ?? "")
			if (contentLength === null) return
			let receivedLength = 0
			const chunks = []
			while (true) {
				const { done, value } = await reader.read()
				if (done) break
				chunks.push(value)
				receivedLength += value.length
				setDownloadProgress(receivedLength / contentLength)
			}

			const allChunks = new Uint8Array(receivedLength)
			let position = 0
			for (const chunk of chunks) {
				allChunks.set(chunk, position)
				position += chunk.length
			}

			const extension = getExtensionByFileName(link.filePath)
			const fileUrl = URL.createObjectURL(new Blob([allChunks]))
			const element = document.createElement("a")
			element.href = fileUrl
			element.download = toSlug(downloadable.name) + (extension !== null ? `.${extension}` : "")
			document.body.appendChild(element)
			element.click()
			URL.revokeObjectURL(fileUrl)
			element.remove()
		} catch (error) {
			console.error(error)
			await alert.show(t("downloadable.downloadFailed"), t("downloadable.downloadFailedMessage"))
		} finally {
			setDownloadProgress(undefined)
		}
	}, [downloadable?.status, downloadable?.name, download, id, alert, t])

	const value = useMemo(() => {
		if (error) return { status: "error" as const }
		if (downloadable === undefined) return { status: "loading" as const }
		if (downloadable === null) return { status: "not-found" as const }

		return {
			downloadProgress,
			download: handleDownload,
			reload: refetch,
			...downloadable,
		}
	}, [error, downloadable, downloadProgress, handleDownload, refetch])

	return value
}
